/** Message contracts between the main thread, the AudioWorklet, and the workers. */
import type { MoodState } from './mood';
import type { Scene, TransitionSpec, TransitionType } from './scene';
import type { Settings } from './settings';
import type { LatencyInfo } from './timing';
import type { TransportState } from './transport';
import type { VisualizationDef } from './viz';

// ---- main → render worker (or main-thread renderer)
export interface RenderInitMessage {
  type: 'init';
  canvas?: OffscreenCanvas;
  width: number;
  height: number;
  dpr: number;
  framePort: MessagePort;
  transportPort: MessagePort;
  scene: Scene;
  settings: Settings;
  latency: LatencyInfo;
  customViz: VisualizationDef[];
}

/** Normalized rectangle (0..1) of the rendered frame. */
export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface OutputConfig {
  /** Part of the frame this output shows (whole frame when mirroring, a tile when tiling). */
  region: Rect;
  rotation: 0 | 90 | 180 | 270;
  brightness: number;
  pattern: 'none' | 'test' | 'identify';
  label: string;
}

export type ToRender =
  | RenderInitMessage
  | { type: 'resize'; width: number; height: number; dpr: number }
  | { type: 'scene'; scene: Scene; transition?: TransitionSpec }
  | { type: 'settings'; settings: Settings }
  | { type: 'latency'; latency: LatencyInfo }
  | { type: 'visibility'; visible: boolean }
  | { type: 'customViz'; defs: VisualizationDef[] }
  | { type: 'midi'; source: string; value: number }
  | { type: 'master'; blackout: boolean; freeze: boolean }
  | { type: 'thumbnail'; id: number; width: number; height: number }
  | {
      type: 'addOutput';
      id: string;
      canvas: OffscreenCanvas | HTMLCanvasElement;
      width: number;
      height: number;
      config: OutputConfig;
    }
  | {
      type: 'updateOutput';
      id: string;
      width?: number;
      height?: number;
      config?: Partial<OutputConfig>;
    }
  | { type: 'removeOutput'; id: string }
  /** Preview mode: the scene being prepared (null ends preview mode). Live is the 'scene'. */
  | { type: 'preview'; scene: Scene | null }
  /**
   * Takes: lock the preview as take `id` and fade it in (timed from `position`, or `manual` for
   * the T-bar). Several can run at once; the newest is on top.
   */
  | {
      type: 'take.start';
      id: number;
      transition: TransitionType;
      durationMs: number;
      manual: boolean;
      position: number;
    }
  /** The T-bar holds take `id` at this position (stops its timer). */
  | { type: 'take.set'; id: number; position: number }
  /** Carry on fading take `id` from where it is, at the rate of a `durationMs` fade. */
  | { type: 'take.resume'; id: number; durationMs: number }
  /** Take `id` is live now (older takes beneath it are dropped). */
  | { type: 'take.complete'; id: number }
  | { type: 'take.cancel'; id: number }
  /** Cut: the preview as it is now goes live over everything. */
  | { type: 'take.cut' }
  /** While recording: send each live frame back as an ImageBitmap of this size (0 stops). */
  | { type: 'recordLive'; width: number; height: number }
  /** The integrity-checked MilkDrop preset pack (JavaScript), evaluated where Butterchurn runs. */
  | { type: 'milkdropPack'; code: string };

export interface RenderStats {
  mode: 'worker' | 'main';
  /** Rendered frames per second. */
  fps: number;
  /** CPU time spent per frame in the renderer, ms (mean over the stats window). */
  cpuMs: number;
  /** Current render scale chosen by the adaptive governor. */
  scale: number;
  /** Effects quality tier (0 = full). */
  tier: number;
  refreshHz: number;
  targetFps: number;
  frames: number;
  /** ms between the newest audio frame and the time being displayed (>0 = relying on prediction). */
  predictMs: number;
  /** Audio level of the displayed frame (0..1), handy for tests and meters. */
  level: number;
  governor: string;
  /** Measured on-screen luminance and the flash limiter's gain. */
  luminance: number;
  flashGain: number;
  /** MB of pooled render targets. */
  poolMB: number;
  /** Live visualizations being drawn (layers × scenes). */
  layers: number;
  modulations: number;
  transition: 'none' | 'waiting' | 'running';
  outputs: number;
  /** Preview mode: 0 = live only, 0..1 = mixing towards the preview, -1 = preview mode off. */
  previewMix: number;
  /** Takes currently fading in (preview mode). */
  takes: number;
  /** What the outputs are showing: the bottom layer's visualization of the live scene. */
  liveVizId: string;
}

export type RenderErrorCode = 'webgl2-unavailable' | 'context-lost' | 'shader-error';

export type FromRender =
  | { type: 'ready'; renderer: string }
  | { type: 'stats'; stats: RenderStats }
  | { type: 'error'; code: RenderErrorCode; message: string }
  | { type: 'thumbnail'; id: number; width: number; height: number; pixels: Uint8Array }
  | { type: 'milkdropPresets'; names: string[] }
  /** The watchdog switched a layer off because frames stalled after it appeared. */
  | { type: 'slowLayer'; layerId: string; name: string }
  /** One live frame for the recorder (see 'recordLive'). */
  | { type: 'liveFrame'; bitmap: ImageBitmap };

// ---- main → analysis worker
export type ToAnalysis =
  | { type: 'init'; onsetPort: MessagePort; transportPort: MessagePort; hopRate: number }
  | { type: 'tap'; time: number }
  | { type: 'reset' }
  | {
      type: 'config';
      bpmMin: number;
      bpmMax: number;
      phraseBars: number;
      /** Follow the music; off holds the tempo until tapped. */
      detect: boolean;
      /** Beats per bar; 0 detects it. */
      beatsPerBar: number;
    }
  /** Beat adjustments: nudge (seconds), half/double tempo, resync (downbeat at time). */
  | { type: 'nudge'; seconds: number }
  | { type: 'scaleTempo'; factor: 0.5 | 2 }
  | { type: 'resync'; time: number }
  /** External clock beat (MIDI clock), in analysis time. */
  | { type: 'externalBeat'; time: number; bpm: number }
  | { type: 'externalStop' }
  /** Pre-analysed beat grid for the playing file, mapped to graph time. */
  | { type: 'grid'; grid: BeatGrid | null }
  | { type: 'mediaClock'; mediaTime: number; graphTime: number; playing: boolean }
  | { type: 'preanalyze'; id: string; sampleRate: number; samples: Float32Array };

export interface BeatGrid {
  /** Seconds per beat. */
  period: number;
  /** Media time (s) of the first beat. */
  firstBeat: number;
  /** Beats per bar (absent in grids from older versions: 4). */
  beatsPerBar?: number;
  /** Beat index (mod beatsPerBar) of downbeats. */
  barOffset: number;
  /** Media times of detected drops. */
  drops: number[];
  /** Section boundaries: media time + label. */
  sections: { time: number; section: string }[];
  /** Overall confidence 0..1. */
  confidence: number;
}

export type FromAnalysis =
  | { type: 'transport'; transport: TransportState; mood: MoodState }
  | { type: 'preanalyzed'; id: string; grid: BeatGrid };

// ---- main → worklet
export const FEATURE_PROCESSOR_NAME = 'glowtrope-features';

export interface WorkletConnectMessage {
  type: 'connect';
  framePort: MessagePort;
  onsetPort: MessagePort;
}

/**
 * Onset messages from the worklet are Float64Array(ONSET_MSG_LENGTH):
 * [time, novelty, hit, rms, bass, high, centroid, kick]
 */
export const ONSET_MSG_LENGTH = 8;
export const ONSET = {
  TIME: 0,
  NOVELTY: 1,
  HIT: 2,
  RMS: 3,
  BASS: 4,
  HIGH: 5,
  CENTROID: 6,
  KICK: 7,
} as const;

/**
 * Where the live monitor sits in the main window in preview mode (fractions of the canvas from
 * the top-left; height follows the aspect ratio). The UI puts its "LIVE" label on the same spot.
 */
export const PREVIEW_MONITOR = { left: 0.018, top: 0.03, width: 0.26 } as const;
